<template>
  <div class="min-h-screen bg-gray-900">
    <!-- Header -->
    <div class="bg-gradient-to-r from-gray-800 to-gray-900 text-white py-16">
      <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h1 class="text-4xl font-bold text-white mb-4">{{ $t('privacy.title') }}</h1>
        <p class="text-xl text-gray-300">{{ $t('privacy.subtitle') }}</p>
      </div>
    </div>

    <!-- Content -->
    <div class="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
      <div class="prose prose-lg max-w-none">
        <!-- Last Updated -->
        <div class="bg-blue-900/30 border-l-4 border-blue-400 p-4 mb-8">
          <p class="text-blue-300">
            <strong>{{ $t('privacy.lastUpdated') }}:</strong> {{ buildDate }}
          </p>
        </div>

        <!-- Introduction -->
        <section class="mb-12">
                      <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.introduction.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.introduction.content') }}</p>
          <p class="text-gray-300">{{ $t('privacy.introduction.contact') }}</p>
        </section>

        <!-- Data Controller -->
        <section class="mb-12">
                      <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.controller.title') }}</h2>
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('privacy.controller.name') }}:</strong> Heidelbell</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('privacy.controller.address') }}:</strong> {{ $t('contact.info.address.value') }}</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('privacy.controller.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300"><strong>{{ $t('privacy.controller.phone') }}:</strong> +49 176 47050093</p>
          </div>
        </section>

        <!-- Data Collection -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.collection.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.collection.intro') }}</p>
          
          <h3 class="text-xl font-semibold text-gray-200 mb-3">{{ $t('privacy.collection.personal.title') }}</h3>
          <ul class="list-disc pl-6 mb-6 text-gray-300">
            <li>{{ $t('privacy.collection.personal.name') }}</li>
            <li>{{ $t('privacy.collection.personal.email') }}</li>
            <li>{{ $t('privacy.collection.personal.subject') }}</li>
            <li>{{ $t('privacy.collection.personal.message') }}</li>
          </ul>

          <h3 class="text-xl font-semibold text-gray-200 mb-3">{{ $t('privacy.collection.automatic.title') }}</h3>
          <ul class="list-disc pl-6 mb-6 text-gray-300">
            <li>{{ $t('privacy.collection.automatic.locale') }}</li>
            <li>{{ $t('privacy.collection.automatic.consent') }}</li>
            <li>{{ $t('privacy.collection.automatic.recaptcha') }}</li>
          </ul>
        </section>

        <!-- Purpose of Data Processing -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.purpose.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.purpose.intro') }}</p>
          
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('privacy.purpose.communication') }}</li>
            <li>{{ $t('privacy.purpose.services') }}</li>
            <li>{{ $t('privacy.purpose.website') }}</li>
            <li>{{ $t('privacy.purpose.legal') }}</li>
            <li>{{ $t('privacy.purpose.spam') }}</li>
          </ul>
        </section>

        <!-- Legal Basis -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.legalBasis.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.legalBasis.intro') }}</p>
          
          <div class="space-y-4">
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h4 class="font-semibold text-gray-200 mb-2">{{ $t('privacy.legalBasis.consent.title') }}</h4>
              <p class="text-gray-300">{{ $t('privacy.legalBasis.consent.content') }}</p>
            </div>
            
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h4 class="font-semibold text-gray-200 mb-2">{{ $t('privacy.legalBasis.contract.title') }}</h4>
              <p class="text-gray-300">{{ $t('privacy.legalBasis.contract.content') }}</p>
            </div>
            
            <div class="bg-gray-800 p-4 rounded-lg border border-gray-700">
              <h4 class="font-semibold text-gray-200 mb-2">{{ $t('privacy.legalBasis.legitimate.title') }}</h4>
              <p class="text-gray-300">{{ $t('privacy.legalBasis.legitimate.content') }}</p>
            </div>
          </div>
        </section>

        <!-- Data Retention -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.retention.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.retention.intro') }}</p>
          
          <ul class="list-disc pl-6 text-gray-300">
            <li>{{ $t('privacy.retention.contact') }}</li>
            <li>{{ $t('privacy.retention.localStorage') }}</li>
            <li>{{ $t('privacy.retention.legal') }}</li>
          </ul>
        </section>

        <!-- Your Rights -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.rights.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.rights.intro') }}</p>
          
          <div class="grid md:grid-cols-2 gap-6">
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">{{ $t('privacy.rights.access.title') }}</h4>
              <p class="text-blue-700 text-sm">{{ $t('privacy.rights.access.content') }}</p>
            </div>
            
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">{{ $t('privacy.rights.rectification.title') }}</h4>
              <p class="text-blue-700 text-sm">{{ $t('privacy.rights.rectification.content') }}</p>
            </div>
            
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">{{ $t('privacy.rights.erasure.title') }}</h4>
              <p class="text-blue-700 text-sm">{{ $t('privacy.rights.erasure.content') }}</p>
            </div>
            
            <div class="bg-blue-50 p-4 rounded-lg">
              <h4 class="font-semibold text-blue-800 mb-2">{{ $t('privacy.rights.portability.title') }}</h4>
              <p class="text-blue-700 text-sm">{{ $t('privacy.rights.portability.content') }}</p>
            </div>
          </div>
        </section>

        <!-- Contact Information -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.contact.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.contact.intro') }}</p>
          
          <div class="bg-gray-800 p-6 rounded-lg border border-gray-700">
            <p class="text-gray-300 mb-2"><strong>{{ $t('privacy.contact.email') }}:</strong> connect@heidelbell.com</p>
            <p class="text-gray-300 mb-2"><strong>{{ $t('privacy.contact.phone') }}:</strong> +49 176 47050093</p>
            <p class="text-gray-300"><strong>{{ $t('privacy.contact.address') }}:</strong> {{ $t('contact.info.address.value') }}</p>
          </div>
        </section>

        <!-- Changes to Privacy Policy -->
        <section class="mb-12">
          <h2 class="text-2xl font-bold text-white mb-4">{{ $t('privacy.changes.title') }}</h2>
          <p class="text-gray-300 mb-4">{{ $t('privacy.changes.content') }}</p>
          <div class="bg-blue-900/30 border-l-4 border-blue-400 p-4">
            <p class="text-blue-300">
              <strong>{{ $t('privacy.lastUpdated') }}:</strong> {{ buildDate }}
            </p>
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<script setup>
import { useI18n } from 'vue-i18n'
import { useBuildDate } from '../composables/useBuildDate'

const { t } = useI18n()
const { buildDate } = useBuildDate()

// SEO Meta tags
document.title = t('privacy.seo.title')
document.querySelector('meta[name="description"]')?.setAttribute('content', t('privacy.seo.description'))
</script>

<style scoped>
.prose {
  @apply text-gray-300 leading-relaxed text-justify;
}

.prose h2 {
  @apply text-2xl font-bold text-white mb-4 mt-8;
}

.prose h3 {
  @apply text-xl font-semibold text-gray-200 mb-3 mt-6;
}

.prose h4 {
  @apply text-lg font-semibold text-gray-200 mb-2 mt-4;
}

.prose .bg-blue-50 h4 {
  @apply text-blue-800;
}

.prose p {
  @apply mb-4;
}

.prose ul {
  @apply mb-6;
}

.prose li {
  @apply mb-2;
}
</style> 